// SZOLGÁLTATÁS + CSOMAG megjelenítése — egy szabály, minden felületen.
//
// A SZABÁLY: a csomag-nevet CSAK akkor írjuk ki, ha ELTÉR a szolgáltatásétól. EGYSZERŰ (simple) szerkezetű
// szolgáltatásnál a katalógus egyetlen csomagot hord, és annak a neve AZONOS a szolgáltatáséval — a kettőt
// egymás mellé téve a felület önmagát ismételte: _„RoomSome Booking engine · RoomSome Booking engine"_.
//
// A legacy CRM is így csinálta (`$isPackage && $planName ? "$planName ($serviceName)" : $serviceName`), és a
// hozzárendelés-lista már helyesen dedupolt — de a szabály HÁROM további helyen hiányzott (figyelem-dialógus,
// bevétel-előrejelzés részlet-modál, generálási kihagyás-napló). Ezért került közös rétegbe: egy szabály,
// amit nem lehet a következő felületen elfelejteni.

import { type TFunction } from 'i18next';

/** A megjelenítendő csomag-név, vagy `null`, ha nincs / megegyezik a szolgáltatáséval (üres nevek is `null`). */
export function distinctPlanName(
  serviceName: string | null | undefined,
  planName: string | null | undefined,
): string | null {
  const plan = planName?.trim();
  if (!plan) return null;
  return plan === serviceName?.trim() ? null : plan;
}

/**
 * EGYSOROS felirat: `szolgáltatás · csomag`, a csomag CSAK ha eltér. Mindkettő üres → `null` (a hívó dönt az
 * em-dash-ről; így a „—" nem ég bele a közös rétegbe).
 */
export function servicePlanLabel(
  serviceName: string | null | undefined,
  planName: string | null | undefined,
  separator = ' · ',
): string | null {
  const service = serviceName?.trim();
  const plan = distinctPlanName(serviceName, planName);
  if (service !== undefined && service !== '' && plan !== null) {
    return `${service}${separator}${plan}`;
  }
  // Az ÜRES string itt hiányt jelent (körülvágás után), ezért nem `??` — a `||` pont ezt a két esetet
  // vonja össze, és a végén `null`-ra esünk (a hívó dönt a „—"-ről).
  return (service === undefined || service === '' ? plan : service) ?? null;
}

/**
 * Egy hozzárendelés AZONOSÍTÓ adatai — annyi, amennyi a nevéhez kell. Szándékosan szűk: a hívó bármelyik
 * sor-alakot átadhatja (lista-sor, detail), és a hiányzó mező egyszerűen elmarad a feliratból.
 */
export interface AssignmentIdentity {
  serviceName: string | null;
  planName: string | null;
  componentCount?: number;
  usageSourceType?: string | null;
  /** A számlázás típusa (`one_time` = eseti díj). */
  billingType?: string | null;
  /** A számlatétel megjegyzése (snapshot) — az ESETI díj pontos megnevezése csak itt áll (pl. setup-díj). */
  invoiceItemNote?: string | null;
}

/**
 * A hozzárendelés MÁSODLAGOS azonosító-sorai, a szolgáltatás-név ALÁ: a csomag (csak ha eltér) · a
 * komplexitás · a használat forrása · eseti díjnál a tétel-megjegyzés.
 *
 * MIÉRT KÖZÖS (user-lelet): ugyanaz a hozzárendelés a hozzárendelés-listán részletesen állt, az
 * ügyfél- és a fiók-detailen viszont sokkal szűkebben — a kezelő nem tudta, ugyanarról a sorról van-e szó.
 * A három felület innentől EGY függvényből veszi a neveket; egy jövőbeli új azonosító-elem is mindháromra
 * egyszerre érvényes.
 *
 * A HASZNÁLAT-FORRÁS felirata a használati adatok lapjának közös kulcs-készletéből jön
 * (`usageData.sourceType`) — abból korábban három másolat élt, és el is tértek egymástól.
 */
export function assignmentSubLines(row: AssignmentIdentity, t: TFunction): string[] {
  const plan = distinctPlanName(row.serviceName, row.planName);
  const sourceKey = row.usageSourceType?.trim();
  const source =
    sourceKey !== undefined && sourceKey !== ''
      ? t(`usageData.sourceType.${sourceKey}`, { defaultValue: sourceKey })
      : null;

  const [keptPlan, keptSource] = dropRedundant(row.serviceName ?? '', plan, source);

  const lines: string[] = [];
  if (keptPlan !== null) lines.push(keptPlan);
  // KOMPLEX szolgáltatás: nem dísz — ilyenkor az ár a KOMPONENSEK összege, nem egy önálló díj. Ez a sor
  // GENERÁLT szöveg, nem katalógus-név: a szó-egyezés nem értelmezhető rá, ezért kimarad a szűrésből.
  const components = row.componentCount ?? 0;
  if (components > 0) {
    lines.push(t('serviceAssignments.complexService', { count: components }));
  }
  if (keptSource !== null) lines.push(keptSource);
  // ESETI DÍJ (user-lelet): a konkrét munka („Cookiebot szoftver beüzemelése …") a tétel-MEGJEGYZÉSBEN áll — a
  // szolgáltatás- és csomag-név általános („Setup díj · Digital marketing"). Mérve élesben: a tétel-NÉV
  // kiírása 2036 sorból 1449-en adott volna (többnyire kevésbé pontos) plusz sort, ezért nem azt írjuk ki, és
  // csak az eseti soron. A sor egy sorban marad (a megjelenítés vágja), a teljes szöveg a szerkesztőben látszik.
  const note = row.billingType === 'one_time' ? row.invoiceItemNote?.trim() : undefined;
  if (note !== undefined && note !== '') lines.push(note);
  return lines;
}

/** Ékezet- és írásjel-mentes szó-darabok — a három név összevetésének egysége. */
function words(value: string): string[] {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w !== '');
}

/**
 * Az a legrövidebb szó-hossz, amitől a PREFIX-egyezést még elhisszük.
 *
 * MIÉRT KELL EGYÁLTALÁN PREFIX: a magyar toldalék ugyanazt a szót adja vissza — a „jutalék" és a
 * „jutalékos" nem két fogalom. Rövid szavakon viszont a prefix véletlen is lehet („ár" ⊂ „árfolyam"),
 * ezért van alsó határ.
 */
const MIN_STEM_LENGTH = 5;

/** Benne van-e a szó a készletben — azonosként vagy magyar toldalékos alakként. */
function isKnown(word: string, pool: readonly string[]): boolean {
  return pool.some(
    (p) =>
      word === p ||
      (word.length >= MIN_STEM_LENGTH &&
        p.length >= MIN_STEM_LENGTH &&
        (word.startsWith(p) || p.startsWith(word))),
  );
}

/**
 * A FÖLÖSLEGES SOROK ELHAGYÁSA: egy név akkor marad el, ha MINDEN szava szerepel már a MEGMARADÓ nevekben.
 *
 * MIÉRT (user-lelet): a három név gyakran ugyanazt mondja háromszor, és a sor olvashatatlanná hízik —
 * _„RoomSome Loyalty / »B« / RoomSome Loyalty"_ vagy _„Hírlevél / Hírlevél havidíj - RoomSome Loyalty /
 * Loyalty hírlevél"_. A szolgáltatás-név HORGONY, azt sosem dobjuk el; a csomag és a forrás viszont
 * elmaradhat, ha semmit nem tesz hozzá.
 *
 * ⚠️ A VIZSGÁLAT A MEGMARADÓ sorok szavaira megy, nem az eredetiekre. Így soha nem veszik el szó: ha a
 * csomag-nevet azért hagytuk el, mert a forrás hordozta, a forrás onnantól NEM hagyható el a csomag-név
 * ürügyén. _(Mérve a teljes éles állományon: 293 forrásos sor, 40 név-hármas — egyik sem veszít szót.)_
 *
 * A SORREND (előbb a csomag) azért ez, mert a FORRÁS a pontosabb: ugyanaz a jutalékos csomag jelenthet
 * direkt foglalást vagy ajánlatot, tehát ha a kettő ugyanazt mondja, a forrás a tartalmasabb.
 */
function dropRedundant(
  serviceName: string,
  plan: string | null,
  source: string | null,
): [string | null, string | null] {
  const anchor = words(serviceName);
  let keptPlan = plan;
  let keptSource = source;

  if (keptPlan !== null) {
    const pool = [...anchor, ...(keptSource === null ? [] : words(keptSource))];
    if (words(keptPlan).every((w) => isKnown(w, pool))) keptPlan = null;
  }
  if (keptSource !== null) {
    const pool = [...anchor, ...(keptPlan === null ? [] : words(keptPlan))];
    if (words(keptSource).every((w) => isKnown(w, pool))) keptSource = null;
  }
  return [keptPlan, keptSource];
}
